// Copyright 2026 The Simlin Authors. All rights reserved.
// Use of this source code is governed by the Apache License,
// Version 2.0, that can be found in the LICENSE file.

import * as React from 'react';

import styles from './DensitySelector.module.css';
import { DENSITY_PRESETS, DensityPreset, densityLabel } from './zone-density';

interface DensitySelectorProps {
  /** The active density preset. */
  density: DensityPreset;
  /** Called with the new preset when the user picks a different one. */
  onChange: (density: DensityPreset) => void;
}

/**
 * The Guide / Support / Focus density switcher (WS8a), a top-sliver segmented
 * control modeled on BuildLoopsPlaySwitch. Density trades chrome for canvas; the
 * order left→right is the gradient (most guidance → most focus).
 *
 * Presentation-only: `density` is a prop and changes flow back through
 * `onChange`, so the Editor stays the single source of truth (it maps the preset
 * to zone sizes via densityConfig).
 */
export class DensitySelector extends React.PureComponent<DensitySelectorProps> {
  render(): React.ReactNode {
    const { density, onChange } = this.props;
    return (
      <div className={styles.switch} role="group" aria-label="Density">
        {DENSITY_PRESETS.map((p) => (
          <button
            key={p}
            type="button"
            className={styles.option}
            aria-pressed={density === p}
            title={`${densityLabel(p)} density`}
            onClick={() => {
              if (density !== p) {
                onChange(p);
              }
            }}
          >
            {densityLabel(p)}
          </button>
        ))}
      </div>
    );
  }
}
